import { useState } from "react";
import {
  useAdminSubscriptions,
  useCancelSubscription,
  useExtendTrial,
  useOverridePlan,
} from "@/hooks/use-admin-subscriptions";
import { useAdminPlans } from "@/hooks/use-admin-plans";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, MoreVertical } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const STATUS_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "active", label: "Ativo" },
  { value: "trialing", label: "Trial" },
  { value: "canceled", label: "Cancelado" },
  { value: "past_due", label: "Inadimplente" },
];

const statusBadgeVariant: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  trialing: "secondary",
  canceled: "destructive",
  past_due: "destructive",
  incomplete: "outline",
  paused: "outline",
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
}

export default function AdminSubscriptions() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAdminSubscriptions({
    status: statusFilter === "all" ? undefined : statusFilter,
    page,
    limit: 20,
  });

  const { data: plans } = useAdminPlans();
  const cancelMutation = useCancelSubscription();
  const extendTrialMutation = useExtendTrial();
  const overridePlanMutation = useOverridePlan();

  const [cancelDialog, setCancelDialog] = useState<string | null>(null);
  const [extendDialog, setExtendDialog] = useState<string | null>(null);
  const [extendDate, setExtendDate] = useState("");
  const [overrideDialog, setOverrideDialog] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState("");

  const handleCancel = () => {
    if (!cancelDialog) return;
    cancelMutation.mutate(
      { subscription_id: cancelDialog },
      { onSuccess: () => setCancelDialog(null) }
    );
  };

  const handleExtendTrial = () => {
    if (!extendDialog || !extendDate) return;
    extendTrialMutation.mutate(
      { subscription_id: extendDialog, new_trial_end: extendDate },
      {
        onSuccess: () => {
          setExtendDialog(null);
          setExtendDate("");
        },
      }
    );
  };

  const handleOverridePlan = () => {
    if (!overrideDialog || !selectedPlanId) return;
    overridePlanMutation.mutate(
      { subscription_id: overrideDialog, plan_id: selectedPlanId },
      {
        onSuccess: () => {
          setOverrideDialog(null);
          setSelectedPlanId("");
        },
      }
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 overflow-x-hidden">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Assinaturas</h2>
        <p className="text-muted-foreground">
          Gerencie todas as assinaturas dos usuarios
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-sm font-medium">
              Filtrar por status
            </CardTitle>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Plano</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden sm:table-cell">Inicio</TableHead>
                      <TableHead className="hidden md:table-cell">Fim Periodo</TableHead>
                      <TableHead className="hidden md:table-cell">Trial Fim</TableHead>
                      <TableHead className="text-right">Acoes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.subscriptions ?? []).map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell>
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{sub.user_name || "-"}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[150px] sm:max-w-none">
                              {sub.user_email}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{sub.plan_name}</TableCell>
                        <TableCell>
                          <Badge variant={statusBadgeVariant[sub.status] ?? "outline"}>
                            {sub.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">{formatDate(sub.created_at)}</TableCell>
                        <TableCell className="hidden md:table-cell">{formatDate(sub.current_period_end)}</TableCell>
                        <TableCell className="hidden md:table-cell">{formatDate(sub.trial_end)}</TableCell>
                        <TableCell className="text-right">
                          {/* Desktop: inline buttons */}
                          <div className="hidden sm:flex gap-1 justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setOverrideDialog(sub.id)}
                            >
                              Alterar Plano
                            </Button>
                            {sub.status === "trialing" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setExtendDialog(sub.id)}
                              >
                                Estender Trial
                              </Button>
                            )}
                            {sub.status !== "canceled" && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setCancelDialog(sub.id)}
                              >
                                Cancelar
                              </Button>
                            )}
                          </div>
                          {/* Mobile: dropdown */}
                          <div className="sm:hidden">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-10 w-10">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setOverrideDialog(sub.id)}>
                                  Alterar Plano
                                </DropdownMenuItem>
                                {sub.status === "trialing" && (
                                  <DropdownMenuItem onClick={() => setExtendDialog(sub.id)}>
                                    Estender Trial
                                  </DropdownMenuItem>
                                )}
                                {sub.status !== "canceled" && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => setCancelDialog(sub.id)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      Cancelar
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(data?.subscriptions ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          Nenhuma assinatura encontrada
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {data && data.total > 20 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground self-center">
                    Pagina {page} de {Math.ceil(data.total / 20)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= Math.ceil(data.total / 20)}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Proxima
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Cancel Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancelar Assinatura</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja cancelar esta assinatura? Esta acao nao pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setCancelDialog(null)} className="w-full sm:w-auto">
              Voltar
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
              className="w-full sm:w-auto"
            >
              {cancelMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Trial Dialog */}
      <Dialog open={!!extendDialog} onOpenChange={() => setExtendDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Estender Trial</DialogTitle>
            <DialogDescription>
              Defina a nova data de fim do trial.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="trial-end">Nova data de fim</Label>
            <Input
              id="trial-end"
              type="date"
              value={extendDate}
              onChange={(e) => setExtendDate(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setExtendDialog(null)} className="w-full sm:w-auto">
              Voltar
            </Button>
            <Button
              onClick={handleExtendTrial}
              disabled={!extendDate || extendTrialMutation.isPending}
              className="w-full sm:w-auto"
            >
              {extendTrialMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override Plan Dialog */}
      <Dialog open={!!overrideDialog} onOpenChange={() => setOverrideDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar Plano</DialogTitle>
            <DialogDescription>
              Selecione o novo plano para o usuario.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Plano</Label>
            <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Selecione um plano" />
              </SelectTrigger>
              <SelectContent>
                {(plans ?? []).map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.name} ({plan.tier})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setOverrideDialog(null)} className="w-full sm:w-auto">
              Voltar
            </Button>
            <Button
              onClick={handleOverridePlan}
              disabled={!selectedPlanId || overridePlanMutation.isPending}
              className="w-full sm:w-auto"
            >
              {overridePlanMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
